import { Button, IconButton } from '@ads/components-react';
import {
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  ExamItemCreateViewModel,
  ExamItemRevisionResultViewModel,
  PostApiV1ExamQuestionLibraryAiCorrectionMutationRequest,
} from '@/shared/api/generated/types';
import { Skeleton } from '@/shared/ui/atoms';
import { useQuestionAiReview } from '../../../hooks/useQuestionAiReview';
import type { QuestionAlternative } from '../../../hooks/useQuestionEdit';
import { areAlternativesChanged, isFieldChanged } from '../../../utils';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Valores atuais do formulário exibidos na coluna "Sua versão". */
export interface QuestionAiReviewCurrent {
  readonly label: string;
  readonly information: string;
  /** Resposta-modelo atual (subtipos ModelAnswerEssay/MbaCase). */
  readonly modelAnswer: string;
  /** Termos-chave atuais (subtipo ModelAnswerEssay). */
  readonly keyTerms: string;
  readonly alternatives: readonly QuestionAlternative[];
  readonly isChoiceType: boolean;
  /** Exibe a comparação de resposta-modelo (subtipo a exige). */
  readonly hasModelAnswer: boolean;
  /** Exibe a comparação de termos-chave (subtipo a aceita). */
  readonly hasKeyTerms: boolean;
}

/** Props do QuestionAiReviewModal. */
export interface QuestionAiReviewModalProps {
  /** Controla a abertura do modal. */
  readonly open: boolean;
  /** Notifica mudança de abertura (fechar). */
  readonly onOpenChange: (open: boolean) => void;
  /** Payload enviado ao endpoint de revisão. */
  readonly request: PostApiV1ExamQuestionLibraryAiCorrectionMutationRequest;
  /** Valores atuais do formulário para comparação. */
  readonly current: QuestionAiReviewCurrent;
  /** Callback ao aplicar a sugestão da IA (tudo-ou-nada). */
  readonly onApply: (result: ExamItemCreateViewModel) => void;
}

// ─── Subcomponentes ─────────────────────────────────────────────────────────

function CompareColumn({
  variant,
  label,
  children,
}: {
  readonly variant: 'current' | 'ai';
  readonly label: string;
  readonly children: ReactNode;
}) {
  const isAi = variant === 'ai';
  return (
    <div
      className={`flex min-w-0 flex-1 flex-col gap-nano p-mini ${
        isAi ? 'border-l-thin border-ctx-content-title' : ''
      }`}
    >
      <div className="flex items-center gap-quark">
        {isAi && <SparklesIcon className="size-xxxs text-ctx-content-title" />}
        <span className="uppercase tracking-wide text-ctx-content-placeholder ts-caption-xxs">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}

function DiffCard({
  title,
  changed,
  changedLabel,
  unchangedLabel,
  current,
  suggestion,
  yourVersionLabel,
  aiSuggestionLabel,
}: {
  readonly title: string;
  readonly changed: boolean;
  readonly changedLabel: string;
  readonly unchangedLabel: string;
  readonly current: ReactNode;
  readonly suggestion: ReactNode;
  readonly yourVersionLabel: string;
  readonly aiSuggestionLabel: string;
}) {
  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
      <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-nano">
        <span className="text-ctx-content-title ts-subtitle-xxs">{title}</span>
        <span
          className={`rounded-pill px-nano py-quark ts-caption-xxs ${
            changed
              ? 'bg-ctx-interactive-secondary text-ctx-content-title'
              : 'bg-ctx-layout-surface text-ctx-content-placeholder'
          }`}
        >
          {changed ? changedLabel : unchangedLabel}
        </span>
      </div>
      <div className="flex">
        <CompareColumn variant="current" label={yourVersionLabel}>
          {current}
        </CompareColumn>
        <CompareColumn variant="ai" label={aiSuggestionLabel}>
          {suggestion}
        </CompareColumn>
      </div>
    </div>
  );
}

function HtmlBlock({ html, empty }: { readonly html: string; readonly empty: string }) {
  const hasContent = html.replace(/<[^>]*>/g, '').trim().length > 0;
  if (!hasContent) {
    return <p className="italic text-ctx-content-placeholder ts-caption-xxs">{empty}</p>;
  }
  return (
    <div
      className="rich-text-content at-scrollbar overflow-x-auto text-ctx-content-base ts-paragraph-xs [&_*]:!text-xs [&_*]:!text-ctx-content-base"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function TextBlock({ text, empty }: { readonly text: string; readonly empty: string }) {
  if (text.trim().length === 0) {
    return <p className="italic text-ctx-content-placeholder ts-caption-xxs">{empty}</p>;
  }
  return <p className="whitespace-pre-wrap text-ctx-content-base ts-paragraph-xs">{text}</p>;
}

function AlternativesBlock({
  alternatives,
  correctLabel,
  empty,
}: {
  readonly alternatives: readonly { value: string; isCorrect: boolean }[];
  readonly correctLabel: string;
  readonly empty: string;
}) {
  if (alternatives.length === 0) {
    return <p className="italic text-ctx-content-placeholder ts-caption-xxs">{empty}</p>;
  }
  return (
    <ul className="flex flex-col gap-nano">
      {alternatives.map((alt, index) => (
        <li key={index} className="flex items-start gap-quark">
          <span
            className={`mt-nano size-nano shrink-0 rounded-circular border-thin ${
              alt.isCorrect
                ? 'border-base-success-600 bg-base-success-600'
                : 'border-ctx-layout-border'
            }`}
            aria-hidden="true"
          />
          <span className="text-ctx-content-base ts-paragraph-xs">
            <span dangerouslySetInnerHTML={{ __html: alt.value }} />
            {alt.isCorrect && (
              <span className="ml-quark text-base-success-700 ts-caption-xxs">
                ({correctLabel})
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Modal de revisão de questão com IA.
 *
 * Ao abrir, envia a questão atual para `POST /api/v1/exam/question-library/ai-correction`
 * e exibe a sugestão em comparação lado a lado ("Sua versão" × "Sugestão da IA")
 * para enunciado, informação complementar e alternativas. A adoção é tudo-ou-nada:
 * "Aplicar sugestões" chama `onApply` com o resultado; "Descartar" fecha sem alterar.
 * Nada é persistido — a gravação continua no botão "Salvar" do formulário.
 *
 * @example
 * ```tsx
 * <QuestionAiReviewModal
 *   open={isReviewOpen}
 *   onOpenChange={setReviewOpen}
 *   request={request}
 *   current={current}
 *   onApply={applyAiReview}
 * />
 * ```
 */
export function QuestionAiReviewModal({
  open,
  onOpenChange,
  request,
  current,
  onApply,
}: QuestionAiReviewModalProps) {
  const { t } = useTranslation('questionLibrary');
  const { mutate, isPending, isError, reset } = useQuestionAiReview();
  const [result, setResult] = useState<ExamItemRevisionResultViewModel | null>(null);

  // A resposta agora envolve a questão revisada em `item` e adiciona `summary`
  // (opinião da IA sobre a questão). A comparação lado a lado usa `item`.
  const suggestion = result?.item;

  const runReview = useCallback(() => {
    setResult(null);
    mutate({ data: request }, { onSuccess: setResult });
  }, [mutate, request]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dispara a revisão apenas na abertura do modal
  useEffect(() => {
    if (open) runReview();
  }, [open]);

  function handleOpenChange(next: boolean) {
    if (!next) {
      setResult(null);
      reset();
    }
    onOpenChange(next);
  }

  function handleApply() {
    if (!suggestion) return;
    onApply(suggestion);
    handleOpenChange(false);
  }

  const status: 'loading' | 'error' | 'success' = isPending
    ? 'loading'
    : isError
      ? 'error'
      : 'success';

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-hairline border-ctx-layout-border px-xxs py-mini">
            <div className="flex items-center gap-nano">
              <div className="flex size-md items-center justify-center rounded-sm bg-ctx-interactive-secondary">
                <SparklesIcon className="size-xs text-ctx-content-title" />
              </div>
              <div className="flex flex-col gap-quark">
                <Dialog.Title className="text-ctx-content-title ts-heading-xs">
                  {t('aiReview.modal.title')}
                </Dialog.Title>
                <Dialog.Description className="text-ctx-content-placeholder ts-caption-xxs">
                  {t('aiReview.modal.subtitle')}
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('aiReview.actions.close')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="at-scrollbar flex flex-1 flex-col gap-mini overflow-y-auto p-xxs">
            {status === 'loading' && (
              <div aria-live="polite" className="flex flex-col gap-mini">
                <div className="flex items-center gap-nano rounded-sm bg-ctx-layout-body p-mini">
                  <ArrowPathIcon className="size-xs animate-spin text-ctx-content-title" />
                  <div className="flex flex-col gap-quark">
                    <strong className="text-ctx-content-title ts-subtitle-xxs">
                      {t('aiReview.states.loadingTitle')}
                    </strong>
                    <span className="text-ctx-content-placeholder ts-caption-xxs">
                      {t('aiReview.states.loadingDescription')}
                    </span>
                  </div>
                </div>
                <Skeleton className="h-[6rem] w-full rounded-sm" />
                <Skeleton className="h-[6rem] w-full rounded-sm" />
              </div>
            )}

            {status === 'error' && (
              <div
                role="alert"
                className="flex items-start gap-micro rounded-sm border-hairline border-base-danger-300 bg-base-danger-100 p-mini text-base-danger-800 ts-paragraph-xxs"
              >
                <ExclamationTriangleIcon className="mt-quark size-xs shrink-0" />
                <div className="flex flex-col gap-quark">
                  <p className="font-semibold">{t('aiReview.states.errorTitle')}</p>
                  <span>{t('aiReview.states.errorDescription')}</span>
                </div>
              </div>
            )}

            {status === 'success' && suggestion && (
              <div aria-live="polite" className="flex flex-col gap-mini">
                {result?.summary && (
                  <div className="flex items-start gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body p-mini">
                    <SparklesIcon className="mt-quark size-xs shrink-0 text-ctx-content-title" />
                    <div className="flex flex-col gap-quark">
                      <strong className="text-ctx-content-title ts-subtitle-xxs">
                        {t('aiReview.summaryTitle')}
                      </strong>
                      <p className="text-ctx-content-base ts-paragraph-xs">{result.summary}</p>
                    </div>
                  </div>
                )}

                <DiffCard
                  title={t('aiReview.fields.label')}
                  changed={isFieldChanged(current.label, suggestion.label)}
                  changedLabel={t('aiReview.compare.changed')}
                  unchangedLabel={t('aiReview.compare.unchanged')}
                  yourVersionLabel={t('aiReview.compare.yourVersion')}
                  aiSuggestionLabel={t('aiReview.compare.aiSuggestion')}
                  current={<HtmlBlock html={current.label} empty={t('aiReview.empty')} />}
                  suggestion={
                    <HtmlBlock html={suggestion.label ?? ''} empty={t('aiReview.empty')} />
                  }
                />

                <DiffCard
                  title={t('aiReview.fields.information')}
                  changed={isFieldChanged(current.information, suggestion.information)}
                  changedLabel={t('aiReview.compare.changed')}
                  unchangedLabel={t('aiReview.compare.unchanged')}
                  yourVersionLabel={t('aiReview.compare.yourVersion')}
                  aiSuggestionLabel={t('aiReview.compare.aiSuggestion')}
                  current={<HtmlBlock html={current.information} empty={t('aiReview.empty')} />}
                  suggestion={
                    <HtmlBlock html={suggestion.information ?? ''} empty={t('aiReview.empty')} />
                  }
                />

                {current.hasModelAnswer && (
                  <DiffCard
                    title={t('aiReview.fields.modelAnswer')}
                    changed={isFieldChanged(current.modelAnswer, suggestion.modelAnswer)}
                    changedLabel={t('aiReview.compare.changed')}
                    unchangedLabel={t('aiReview.compare.unchanged')}
                    yourVersionLabel={t('aiReview.compare.yourVersion')}
                    aiSuggestionLabel={t('aiReview.compare.aiSuggestion')}
                    current={<TextBlock text={current.modelAnswer} empty={t('aiReview.empty')} />}
                    suggestion={
                      <TextBlock text={suggestion.modelAnswer ?? ''} empty={t('aiReview.empty')} />
                    }
                  />
                )}

                {current.hasKeyTerms && (
                  <DiffCard
                    title={t('aiReview.fields.keyTerms')}
                    changed={isFieldChanged(current.keyTerms, suggestion.keyTerms)}
                    changedLabel={t('aiReview.compare.changed')}
                    unchangedLabel={t('aiReview.compare.unchanged')}
                    yourVersionLabel={t('aiReview.compare.yourVersion')}
                    aiSuggestionLabel={t('aiReview.compare.aiSuggestion')}
                    current={<TextBlock text={current.keyTerms} empty={t('aiReview.empty')} />}
                    suggestion={
                      <TextBlock text={suggestion.keyTerms ?? ''} empty={t('aiReview.empty')} />
                    }
                  />
                )}

                {current.isChoiceType && (
                  <DiffCard
                    title={t('aiReview.fields.alternatives')}
                    changed={areAlternativesChanged(
                      current.alternatives,
                      suggestion.examTypeList?.examTypeListValues,
                    )}
                    changedLabel={t('aiReview.compare.changed')}
                    unchangedLabel={t('aiReview.compare.unchanged')}
                    yourVersionLabel={t('aiReview.compare.yourVersion')}
                    aiSuggestionLabel={t('aiReview.compare.aiSuggestion')}
                    current={
                      <AlternativesBlock
                        alternatives={current.alternatives.map(alt => ({
                          value: alt.value,
                          isCorrect: alt.isCorrect,
                        }))}
                        correctLabel={t('aiReview.correct')}
                        empty={t('aiReview.empty')}
                      />
                    }
                    suggestion={
                      <AlternativesBlock
                        alternatives={(suggestion.examTypeList?.examTypeListValues ?? []).map(
                          v => ({
                            value: v.value ?? '',
                            isCorrect: v.correctAnswer ?? false,
                          }),
                        )}
                        correctLabel={t('aiReview.correct')}
                        empty={t('aiReview.empty')}
                      />
                    }
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t-hairline border-ctx-layout-border px-xxs py-mini">
            <div className="flex items-center gap-quark text-ctx-content-placeholder ts-caption-xxs">
              <InformationCircleIcon className="size-xxs" />
              <span>{t('aiReview.footerNote')}</span>
            </div>
            <div className="flex items-center gap-sm">
              {status === 'error' ? (
                <Button hierarchy="primary" leadingIcon={ArrowPathIcon} onClick={runReview}>
                  {t('aiReview.actions.retry')}
                </Button>
              ) : (
                <>
                  <Dialog.Close asChild>
                    <Button hierarchy="secondary">{t('aiReview.actions.discard')}</Button>
                  </Dialog.Close>
                  <Button
                    hierarchy="primary"
                    leadingIcon={CheckIcon}
                    disabled={status !== 'success' || !suggestion}
                    onClick={handleApply}
                  >
                    {t('aiReview.actions.apply')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
